import { extendTheme } from '@chakra-ui/react';

const inputFieldStyle = {
  bg: 'white',
  borderWidth: '1px',
  borderColor: 'gray.400',
  borderRadius: 'sm',
  _disabled: {
    bg: 'gray.50',
    borderColor: 'gray.50',
    opacity: 1,
  },
  _readOnly: {
    bg: 'gray.50',
    borderColor: 'gray.50',
    cursor: 'not-allowed',
  },
  _focus: {
    outline: '2px solid',
    outlineColor: 'snap.teal.600',
    outlineOffset: '0px',
  },
  _invalid: {
    _focus: {
      outline: '2px solid',
      outlineColor: 'snap.red.600',
    },
  },
};

export const colors = {
  snap: {
    gray: {
      100: '#e6e5e5',
    },
    red: {
      600: 'hsl(8 100% 47% / 1)',
      '600/10': 'hsl(8 100% 47% / 0.1)',
      '600/20': 'hsl(8 100% 47% / 0.2)',
      700: '#A20C33',
      800: '#720925',
    },
    teal: {
      50: '#E6F8F9',
      100: '#ABD5CD',
      200: '#8AC3BC',
      300: '#69B1AB',
      400: '#499F9C',
      500: '#2A8A8C',
      600: '#0b7178',
      '600/10': 'hsl(184 83.2% 25.7% / 0.1)',
      '600/20': 'hsl(184 83.2% 25.7% / 0.2)',
      700: '#08606B',
      800: '#05505D',
      900: '#03404F',
    },
    yellow: {
      600: 'hsl(50 100% 50%)',
      '600/10': 'hsl(50 100% 50% / 0.1)',
      '600/20': 'hsl(50 100% 50% / 0.2)',
      700: '#ccaa00',
    },
  },
  mw: {
    yellow: '#FFD600',
    darkYellow: '#ebc603',
    black: '#001223',
    grey: '#465B65',
    darkGrey: '#273b46',
    lighterGrey: '#D9DEE2',
    lightGrey: '#BBBABA',
    panelGrey: 'rgb(238,240,242)',
    blue: '#00A8B5',
    darkBlue: '#007B7B',
    orange: '#F78B21',
    darkOrange: '#F36B21',
    red: '#EE3341',
    darkRed: '#A20C33',
    green: '#BFD730',
    darkGreen: '#6E9934',
    white: '#FFFFFF',
  },
  validation: {
    error: '#f22000',
    warning: '#d08700',
    info: '#00A8B5',
  },
  input: {
    icon: '#999',
  },
};

export const SANS_STACK = 'Source Sans 3 Variable, system-ui, sans-serif';

const baseMWTheme = {
  fonts: {
    body: SANS_STACK,
    heading: SANS_STACK,
    navigationItem: SANS_STACK,
    mono: 'Menlo, monospace',
  },
  fontWeights: {
    hairline: 100,
    thin: 200,
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
    black: 900,
  },
  lineHeights: {
    normal: 'normal',
    none: 1,
    shorter: 1.25,
    short: 1.375,
    base: 1.5,
    tall: 1.625,
    taller: '2',
    '3': '.75rem',
    '4': '1rem',
    '5': '1.25rem',
    '6': '1.5rem',
    '7': '1.75rem',
    '8': '2rem',
    '9': '2.25rem',
    '10': '2.5rem',
  },
  colors,
  fontSizes: {
    xs: '0.75rem',
    sm: '0.875rem',
    md: '1rem',
    lg: '1.125rem',
    xl: '1.25rem',
    '2xl': '1.5rem',
    '3xl': '1.875rem',
    '4xl': '2.25rem',
    '5xl': '3rem',
    '6xl': '3.75rem',
    '7xl': '4.5rem',
    '8xl': '6rem',
    '9xl': '8rem',
  },
  letterSpacings: {
    tighter: '-0.05em',
    tight: '-0.025em',
    normal: '0',
    wide: '0.025em',
    wider: '0.05em',
    widest: '0.1em',
  },
  // Base Components styles
  components: {
    Input: {
      baseStyle: {
        field: {
          ...inputFieldStyle,
        },
      },
      variants: {
        combobox: {
          field: {
            backgroundColor: 'white',
            borderColor: 'mw.lightGrey',
            borderRadius: 0,
            outlineOffset: 0,
            outline: '1px solid transparent',
            pr: 10,
            _hover: {
              borderColor: 'mw.blue',
            },
            _focus: {
              outlineColor: 'mw.blue',
            },
            _invalid: {
              borderColor: 'snap.red.600',
              backgroundColor: 'snap.red.600/20',

              _hover: {
                backgroundColor: 'snap.red.600/10',
              },

              _focus: {
                backgroundColor: 'snap.red.600/10',
                outlineColor: 'snap.red.600',
              },
            },
          },
        },
        default: {
          field: {},
        },
      },
      defaultProps: {
        variant: 'default',
      },
    },
    NumberInput: {
      baseStyle: {
        field: {
          ...inputFieldStyle,
        },
      },
      variants: {
        default: {
          field: {},
        },
      },
      defaultProps: {
        variant: 'default',
      },
    },
    Select: {
      variants: {
        default: {
          field: {
            ...inputFieldStyle,
          },
        },
      },
      defaultProps: {
        variant: 'default',
      },
    },
    Textarea: {
      baseStyle: {
        ...inputFieldStyle,
      },
      variants: {
        default: {
          field: {},
        },
      },
      defaultProps: {
        variant: 'default',
      },
    },
    Checkbox: {
      baseStyle: {
        control: {
          ...inputFieldStyle,
        },
      },
      variants: {
        default: {
          field: {},
        },
      },
      defaultProps: {
        variant: 'default',
      },
    },
    Button: {
      baseStyle: {
        rounded: 'sm',
      },
      variants: {
        destructive: {
          color: 'white',
          backgroundColor: 'snap.red.700',
          borderColor: 'snap.red.700',
          _hover: {
            color: 'snap.red.100',
            backgroundColor: 'snap.red.800',
          },
        },
        'destructive-outline': {
          borderWidth: '1px',
          borderStyle: 'solid',
          color: 'snap.red.700',
          borderColor: 'snap.red.700',
          _hover: {
            color: 'white',
            backgroundColor: 'snap.red.700',
            borderColor: 'snap.red.700',
          },
        },
      },
    },
  },
};

export type Breakpoints = 'base' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

export const breakpoints: Record<Breakpoints, string> = {
  base: '0em', // 0px
  sm: '30em', // 480px
  md: '48em', // 768px
  lg: '62em', // 992px
  xl: '80em', // 1280px
  '2xl': '96em', // 1536px
};

export const theme = extendTheme({ ...baseMWTheme, ...breakpoints });
